// Imports ------------------------------------------------------------------->
import { attr, Component } from '../Component'
import { element } from '../CustomElement'
import { i18n } from '../../i18n/I18n'
import cx from 'classnames'

import { Dialog } from '../flipp-dialog/Dialog'
import { PageContext } from '../flipp-page/Page'
import { SearchBox } from '../flipp-searchbox/SearchBox'
import { AccountTriggerElement } from '../flipp-account-trigger/AccountTriggerElement'
import { ShoppingListTriggerElement } from '../flipp-shopping-list-trigger/ShoppingListTriggerElement'

import { inject } from '../../services/di'
import { AnalyticsService } from '../../services/AnalyticsService'
import { ConfigService, RuntimeConfig } from '../../services/ConfigService'
import { UserAgentService } from '../../services/UserAgentService'
import { StorageService } from '../../services/StorageService'
import { HistoryService } from '../../services/HistoryService'

import { AutocompleteContext } from '../../definitions/CommonDefinitions'
import { AuthenticationService } from '../../services/AuthenticationService'
import { EventService } from '../../services/EventService'
import { LinkService } from '../../services/LinkService'
import { LocationService } from '../../services/LocationService'
import { DataFormService } from '../../services/DataFormService'
import { BannerService } from '../../services/BannerService'
import {
    BlackFridayService,
    GiveawayPeriod,
} from '../../services/BlackFridayService'
import { icons } from '../../icons/Icons'
import { renderDeleteAccountModal } from '../../utils/modal'
import { LocationPicker } from '../flipp-location-picker/LocationPicker'

// Private Definitions ------------------------------------------------------->

// Public Definitions -------------------------------------------------------->

export interface ApplicationTopBarProps extends JSX.HTMLProps {
    search?: string
    context?: PageContext
}

@element('flipp-application-topbar')
export class ApplicationTopBar extends Component<ApplicationTopBarProps, any> {
    @attr search: string
    @attr context: PageContext

    @inject userAgent: UserAgentService
    @inject storage: StorageService
    @inject analytics: AnalyticsService
    @inject config: ConfigService
    @inject history: HistoryService
    @inject auth: AuthenticationService
    @inject eventService: EventService
    @inject linkService: LinkService
    @inject dataFormService: DataFormService
    @inject locationService: LocationService
    @inject bannerService: BannerService
    @inject bf: BlackFridayService

    refs: {
        companyMenu: Dialog
        settings: HTMLButtonElement
    }

    state = {
        aboutFlippMenuOpened: false,
    }

    onAttach() {
        window.addEventListener(
            'unsubscribe-from-redirect',
            this.unsubscribeFromRedirect
        )
        this.addEventListener('close', this._handleDialogCloseEvent)
    }

    onDetach() {
        window.removeEventListener(
            'unsubscribe-from-redirect',
            this.unsubscribeFromRedirect
        )
        this.removeEventListener('close', this._handleDialogCloseEvent)
    }

    renderGiveawayBanner(giveawayPeriod: string) {
        if (
            !giveawayPeriod ||
            (giveawayPeriod &&
                ![
                    GiveawayPeriod.BLACK_FRIDAY,
                    GiveawayPeriod.CHRISTMAS,
                ].includes(giveawayPeriod))
        )
            return null
        const title =
            giveawayPeriod === GiveawayPeriod.BLACK_FRIDAY
                ? i18n('giveaway_title')
                : giveawayPeriod === GiveawayPeriod.CHRISTMAS
                  ? i18n('giveaway_christmas_title')
                  : ''
        const subtitle =
            giveawayPeriod === GiveawayPeriod.BLACK_FRIDAY
                ? i18n('giveaway_banner_date')
                : giveawayPeriod === GiveawayPeriod.CHRISTMAS
                  ? i18n('giveaway_christmas_subtitle')
                  : ''
        const buttonText =
            giveawayPeriod === GiveawayPeriod.BLACK_FRIDAY
                ? i18n('giveaway_banner_button_text')
                : giveawayPeriod === GiveawayPeriod.CHRISTMAS
                  ? i18n('giveaway_christmas_button_text')
                  : ''
        return (
            <div class={cx('giveaway', { bf: this.bf.isOnHost() })}>
                {icons.deals}
                <div class="giveaway__text">
                    <div class="giveaway__title">{title}</div>
                    <div class="giveaway__date">{subtitle}</div>
                </div>
                <a
                    href={this.bf.getGiveawayLink()}
                    target="_blank"
                    class="giveaway-banner__button"
                    id="giveaway-banner-top__button"
                >
                    {buttonText}
                    {icons.rightArrow}
                </a>
            </div>
        )
    }

    render() {
        if (!this.config.showAppTopBar) {
            return null
        }
        const appLink = this.userAgent.getAppLinkHref()
        /* For distribution we need to ensure the Help link is https. This is because if the
         * parent page is loaded in https, browsers will block the transition as it's "insecure".
         * Our freshdesk URL is cname'd to the flipp.com domain which doesn't use https. So for
         * distribution, use the full Freshdesk URL with https until we can have a proper certificate
         * on the cname'd version
         * */
        const helpLink =
            this.config.RUNTIME === RuntimeConfig.DISTRIBUTION
                ? 'https://flipp.freshdesk.com/support/home'
                : i18n('help_link')

        const suppressedAppTopBar = !this.config.suppressedAppTopBar

        const urlParams = new URLSearchParams(window.location.search)
        const giveaway = urlParams.get('giveaway') // 'black_friday', 'christmas', 'back_to_school'
        const giveawayPeriod = giveaway
            ? giveaway.toUpperCase()
            : this.bf.getGiveawayPeriod()

        return (
            <template>
                <header
                    flex-noshrink
                    flex-horizontal
                    class={cx({ bf: this.bf.isOnHost() })}
                    style={{
                        backgroundColor: this.bf.isOnHost()
                            ? 'auto'
                            : this.config.getThemeColor(),
                    }}
                >
                    <content />
                    <div
                        class="suppressed-app-top-bar"
                        style={{
                            backgroundColor: this.bf.isOnHost()
                                ? 'auto'
                                : this.config.getThemeColor(),
                        }}
                    >
                        {suppressedAppTopBar ? (
                            <SearchBox
                                search={this.search}
                                autocompletecontext={
                                    AutocompleteContext.GLOBAL_SEARCH
                                }
                                flex-grow
                                flex-horizontal
                                flex-noshrink
                            />
                        ) : null}
                        {this.renderGiveawayBanner(giveawayPeriod)}
                        <div flex-horizontal anchor-point="right bottom">
                            <div
                                class="links hidden"
                                flex-horizontal
                                flex-noshrink
                            >
                                <a
                                    href="https://www.flippappinions.com/c/a/6kiwEIx1Ce287lz8RyHYr8"
                                    target="blank"
                                >
                                    <button class="settings">
                                        {this.bf.isOnHost() ? (
                                            <img
                                                src="https://f.wishabi.net/creative/Distribution/images/bf/feedback+1.png"
                                                height="20px"
                                                alt=""
                                            />
                                        ) : (
                                            <img
                                                src="https://f.wishabi.net/creative/Distribution/images/icons/feedback-default5.svg"
                                                height="26px"
                                                alt=""
                                            />
                                        )}
                                        <span>{i18n('give_feedback')}</span>
                                    </button>
                                </a>
                            </div>
                            {this.getGetAppButton()}
                            <LocationPicker />
                        </div>
                        {suppressedAppTopBar && !this.userAgent.mobile ? (
                            <ShoppingListTriggerElement
                                selected={this.context === 'shoppingList'}
                            />
                        ) : null}
                        <div
                            class={`links ${this.state.aboutFlippMenuOpened ? 'opened' : ''} ${this.bf.isOnHost() ? 'bf' : ''}`}
                            flex-horizontal
                            flex-noshrink
                            ref="settings"
                            anchor-point="right bottom"
                        >
                            <button
                                class="settings"
                                onClick={this.handleCompanyMenuClicked}
                            >
                                {this.bf.isOnHost() ? (
                                    <img
                                        src="https://f.wishabi.net/creative/Distribution/images/bf/info+1.png"
                                        height="20px"
                                        alt=""
                                    />
                                ) : (
                                    icons.info2
                                )}
                                <span>{i18n('about_flipp')}</span>
                                <div class="chevron">
                                    {this.state.aboutFlippMenuOpened
                                        ? icons.chevronUp
                                        : icons.chevronDown}
                                </div>
                                <Dialog
                                    class="settings-dialog popover"
                                    ref="companyMenu"
                                    anchor-point="right top"
                                >
                                    <div class="arrow"></div>
                                    <hr />
                                    <a
                                        target="_blank"
                                        href="https://corp.flipp.com"
                                    >
                                        {i18n('company')}
                                    </a>
                                    <a
                                        target="_blank"
                                        href={i18n('about_us_link')}
                                    >
                                        {i18n('about_us')}
                                    </a>
                                    <a
                                        target="_blank"
                                        href="https://corp.flipp.com/careers-culture/"
                                    >
                                        {i18n('careers')}
                                    </a>
                                    <a
                                        target="_blank"
                                        href={appLink}
                                        onClick={() => {
                                            this.handleAppLinkClick_()
                                        }}
                                    >
                                        {i18n('mobile_app')}
                                    </a>
                                    <a target="_blank" href={helpLink}>
                                        {i18n('help')}
                                    </a>
                                    <hr />
                                    <a target="_blank" href={i18n('terms_url')}>
                                        {i18n('terms_of_use')}
                                    </a>
                                    <a
                                        target="_blank"
                                        href={i18n('privacy_url')}
                                    >
                                        {i18n('privacy_policy')}
                                    </a>
                                    <button
                                        class="data-button"
                                        onClick={
                                            this.dataFormService
                                                .handleFormClicked
                                        }
                                    >
                                        {i18n('my_data')}
                                    </button>
                                    <button
                                        class="data-button"
                                        onClick={this.openDeleteAccountPopup}
                                    >
                                        {i18n('delete_account_link')}
                                    </button>
                                    <a
                                        target="_blank"
                                        href="https://corp.flipp.com/legal/patents/"
                                    >
                                        {i18n('patents')}
                                    </a>
                                    <a
                                        target="_blank"
                                        href="https://corp.flipp.com/legal/privacy/#AdChoices"
                                    >
                                        {i18n('ad_choices')}
                                    </a>
                                    <hr />
                                </Dialog>
                            </button>
                        </div>
                        {suppressedAppTopBar &&
                        !this.bannerService.isSmartNews() ? (
                            <AccountTriggerElement />
                        ) : null}
                    </div>
                </header>
            </template>
        )
    }

    private getGetAppButton = () => {
        const visibilityClass = this.bannerService.isSmartNews()
            ? 'get-app-wrapper hid'
            : 'get-app-wrapper'
        const buttonClass =
            this.locationService.locale === 'fr-ca' ? 'french' : ''
        return (
            <div class={visibilityClass}>
                <div class="get-app-button">
                    <a href="https://click.flipp.com/h8pE/35d6c38b">
                        <button class={buttonClass}>{i18n('get_app')}</button>
                    </a>
                </div>
            </div>
        )
    }

    private handleAppLinkClick_ = () => {
        if (this.userAgent.android || this.userAgent.iOS) {
            this.analytics.trackMobileInstall(
                this.userAgent.android ? 'android' : 'ios'
            )
        }
    }

    _handleDialogCloseEvent() {
        this.state.aboutFlippMenuOpened = false
        this.invalidate()
    }

    private openDeleteAccountPopup = () => {
        window.history.pushState({}, '', 'data-deletion')
        this.refs.companyMenu.close()
        renderDeleteAccountModal('flyers')
    }

    private handleCompanyMenuClicked = () => {
        this.refs.companyMenu.showModal(this.refs.settings)
        this.state.aboutFlippMenuOpened = true
        this.invalidate()
    }

    private unsubscribeFromRedirect = () => {
        window.removeEventListener('redirect-to-form', this.redirectToForm)
    }

    private redirectToForm = () => {
        this.linkService.execute('/data', this)
    }
}
